import React, { useState, useEffect } from 'react'
import { Fade, Modal } from '@mui/material'
import View from '@modul-connect/shared/components/atoms/view'
import Button from '@modul-connect/shared/components/atoms/button'
import { H1, P } from '@modul-connect/shared/components/atoms/text'
import modal from '@modul-connect/shared/components/atoms/modal'
import theme from '@modul-connect/shared/theme'
import TableWidget from '@modul-connect/shared/components/molecules/tableWidget'
import { userModal_columns } from './team_columns'
import { useDispatch, useSelector } from 'react-redux'
import { SearchField } from '@modul-connect/shared/components/molecules/searchWidget.v2/searchField'
import { useAuth0 } from '@auth0/auth0-react'
import { fetchUsers } from '../../../state/actions/users'
import { ModalAlertBanner } from './modalAlertBanner'
import { BubbleLoadingWidget } from '@modul-connect/shared/components/molecules/bubbleLoadingWidget/bubbleLoadingWIdget'

export const AddTeamUserModal = ({ 
  openModal,
  uuid,
  onClose,
  disabled,
  userExists,
  moveUser,
  parent,
  isLoading
}) => {
  const [userPosted, setUserPosted] = useState(false)

  const [user, setUser] = useState('')
  const [selectedUsers, setSelectedUsers] = useState([])
  const [totalUsers, setTotalUsers] = useState(0)

  const users = useSelector(state => state.users.data)
  const usersCount = useSelector(state => state.users.total)
  const loading = useSelector(state => state.loading)
  const nodeUsers = useSelector(state => state.organisation_node.users)
  const authenticatedUser = useSelector(state => state.authenticated_user.email)
  const failedUsers = useSelector(state => state.organisation_node.userErrors)
  const failedEmails = failedUsers.map(u => u.email)
  const isRetry = failedEmails.length > 0
    
  const dispatch = useDispatch()

  const { getAccessTokenSilently } = useAuth0()

  useEffect(() => {
    if (openModal) {
      setUserPosted(false)
      setSelectedUsers([])
      return
    }

    setUser('')
  }, [openModal])

  useEffect(() => {
    if (failedUsers.length === 0) return
    setTotalUsers(prev => Math.max(prev, failedUsers.length))
  }, [failedUsers])

  const onSave = () => {
    if (selectedUsers.length === 0 && failedUsers.length === 0) {
      return
    }

    const failedEmails = failedUsers.map(u => u.email)
    const isRetry = failedEmails.length > 0

    const usersToSave = isRetry
      ? selectedUsers.filter(u => failedEmails.includes(u.email))
      : selectedUsers

    setTotalUsers(usersToSave.length)
    usersToSave.forEach((user) => {
      moveUser({
        name: user.name,
        email: user.email,
        uuid: user.uuid, 
        privilege: 'standard'
      })
      setUserPosted(true)
    }
    )
  }

  const users_prepared = users ? users.map(user => ({
    ...user,
    name: `${user.firstName} ${user.lastName}`,
  }))
    : []
  
  const userRows = users_prepared.map(u => ({
    id: u.email, 
    name: u.name,
    email: u.email, 
  }))
    .filter(u => !nodeUsers.find(user => user.email === u.email))
    .filter(u => u.email !== authenticatedUser)

  const toggleSelectedRow = (row, prevSelected) => {
    const alreadySelected = prevSelected.find(u => u.id === row.id)
    return alreadySelected
      ? prevSelected.filter(u => u.id !== row.id)
      : [...prevSelected, row]
  }

  let filterTimer = null

  const setFilterAfterDelay = (newFilter) => {
    if (filterTimer) {
      clearTimeout(filterTimer)
    }

    filterTimer = setTimeout(() => {
      setUser(newFilter)
    }, 1000)
  }

  const failedUserRows = failedUsers.map(u => ({
    id: u.email,
    name: u.name ?? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
    email: u.email,
    isFailed: true,
    code: u.code
  }))



  const handleNameChange = (event) => {
    setFilterAfterDelay(event.target.value)
  }
  
  const fetchUsersWithAccessToken = (options) => {
    getAccessTokenSilently()
      .then(accessToken => dispatch(fetchUsers(accessToken, options)))
  }

  const columns = React.useMemo(() => [
    ...userModal_columns,
    {
      id: 'error',
      label: '',
      minWidth: 200,
      maxWidth: 200,
      width: 160,
      hide: [],
      disableSort: true,
      render: (_val, row) => {
        const failedUser = failedUsers.find(u => u.email === row.email)
        if (!failedUser) return
        const msg = 'Failed to add user'
        const code = failedUser.code     
        return (
          <span style={{ color: theme.colors.darkRed }}>
            {`${msg} (${code}).`}
          </span>
        )
      },
    },
  ], [failedUsers])

  const handleClickRow = (_e, _id, row) => {
    if (isRetry || isLoading ) return

    setSelectedUsers(prev => toggleSelectedRow(row, prev))  
    // const isFailed = failedUsers.some(u => u.email === row.email);
    // if (isFailed) {
    //   dispatch({ type: 'CLEAR_FAILED_USER', payload: row.email });
    // }
  }

  
  return (
    <Modal
      open={openModal}
      onClose={(event, reason) => {
        if (disabled && reason === 'backdropClick') return
        dispatch({ type: 'CLEAR_ALL_ERRORS' })
        onClose()
      }}
    >
      <Fade in={openModal}>
        <div>
          <View extend={{
            ...modal.container,
            width: '70%',
            height: '80%',
            position: 'absolute',
            top: '8%',
            left: '50%',
            transform: 'translateX(-50%)',
            '@media (max-width: 1025px)': {
              width: '94%',
              left: '50%',
              right: 'auto',     
              transform: 'translateX(-50%)',
            },
          }}>
            <View extend={modal.header}>
              <H1 text="Add user to team" />
              {failedUsers.length > 0 ?
                <ModalAlertBanner text={`${failedUsers.length} of ${totalUsers} users could not be added, please try again.`}/>
                : ''
              }
            </View>
            <View extend={{
              ...modal.content,
              maxHeight: '60vh',      
              overflowY: 'auto',
            }}>
              <>
                {userPosted && userExists && (
                  <P large style={{ color: theme.colors.error }}>
                      User already exists.
                  </P>
                )}
                <SearchField
                  extend={{ backgroundColor: theme.colors.lightGrey }}
                  onChange={handleNameChange}
                  placeholder={'Search user...'}
                  initialValue={user}
                />

                <TableWidget
                  data={failedUserRows?.length ? failedUserRows : userRows}
                  columns={columns}
                  totalItems={failedUserRows.length > 0 ? failedUserRows.length : usersCount}
                  defaultSort="name"
                  searchStr={user}
                  rowsOptions={failedUserRows.length > 0 ? [1000]: undefined}
                  disableClickCondition={(row) => isLoading || (isRetry && !failedUsers.some(f => f.email === row.email))}
                  errorRows={failedUsers.map(e => {
                    return { id: e.email }
                  })}                  
                  themes={{ device: 'desktop' }}
                  extend={{ paddingTop: 10 }}
                  pagination={failedUserRows.length > 0 ? false : true}
                  loadingStatus={loading?.fetchUsers}
                  selectedRows={selectedUsers.map(u => u.email)}
                  onClickRow={handleClickRow}
                  onFetchData={(options) => {
                    fetchUsersWithAccessToken({ ...options, organisation: parent.uuid, noAdmin: true, filternode: uuid, ignoreSelectedOrgs: true })

                  }}  
                />
              </>
            </View>
            <View style={{ height: 60, borderTop: `1px solid ${ theme.colors.lightGrey }`, display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
              <View>
                {isLoading && (
                  <BubbleLoadingWidget horizontal text={'Loading ... '} />
                )}
              </View>
              <View style={{ display: 'flex', flexDirection: 'row', }}>
                <Button noBg disabled={disabled} cancel onClick={() => {
                  dispatch({ type: 'CLEAR_ALL_ERRORS' })
                  onClose()
                }}>
                  Cancel
                </Button>
                <Button noBg disabled={disabled || (isRetry ? failedUsers.length === 0 : selectedUsers.length === 0) }noPadding onClick={onSave}>
                  {failedUsers.length  > 0 ? `Retry (${failedUsers.length})` : `Submit (${selectedUsers.length})` }
                </Button>
              </View>
            </View>
          </View>
        </div>
      </Fade>
    </Modal>
  )
}

export default AddTeamUserModal
